import React, { useMemo } from "react";
import { DrawerModal } from "../drawer-modal/drawer-modal";
import styles from "./edit-widget-drawer-modal.module.scss";
import { Title } from "@src/components/atoms/title/title";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { MachineOverviewEditor } from "./machine-overview/machine-overview-editor";
import { ShotDataLineChartEditor } from "./shot-data-line-chart/shot-data-line-chart-editor";
import { StaticHeaderEditor } from "./static-header/static-header-editor";
import { StaticTextEditor } from "./static-text/static-text-editor";
import { StaticLinkEditor } from "./static-link/static-link-editor";
import { StaticSeparatorEditor } from "./static-separator/static-separator-editor";
import { CounterNumberEditor } from "./counter-number/counter-number-editor";
import { TrendLineEditor } from "./trend-line/trend-line-editor";
import { MultiLineEditor } from "./multi-line/multi-line-editor";
import { SingleTextValueEditor } from "./single-text-value/single-text-value-editor";
import { MultipleTextValuesEditor } from "./multiple-text-values/multiple-text-values-editor";
import { useDispatch, useSelector } from "react-redux";
import {
  updateEditWidgetDrawerModalTileId,
  updateModalsCloseDrawerModal,
} from "@src/reducers/your-reducer";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import { Widget } from "@src/types/dashboards";
import { removePendingWidget } from "@src/reducers/dashboards-slice";

const cssClassName = "edit-widget-drawer-modal";

export const EditWidgetDrawerModal = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const { tileId } = useSelector(
    (state: RootState) => state.yourSlice.modals.editWidgetDrawerModal,
  );
  const selectedDashboard = useSelector((state: RootState) =>
    state.dashboards.dashboards.find(
      (d) => d.id === state.dashboards.selectedDashboardId,
    ),
  );
  const widget = useMemo<Widget | undefined>(() => {
    if (!tileId || !selectedDashboard) return undefined;
    return selectedDashboard.widgets[tileId];
  }, [tileId, selectedDashboard]);

  // Pick-then-save flow: the picker created a widget locally with a
  // `pending-{uuid}` id; the BE has never seen it. Save commits (POST +
  // PUT); close-without-save cleans up the local tile via handleClose.
  const pendingWidgetIds = useSelector(
    (state: RootState) => state.dashboards.pendingWidgetIds,
  );
  const isPendingWidget =
    widget != null && pendingWidgetIds.includes(widget.id);

  const handleRemovePendingWidget = () => {
    // A mid-save X click drops the local pending tile but lets the in-flight
    // POST finish on the server. If it succeeds the trailing
    // commitPendingWidget(tempId → realId) + dashboard refresh surfaces the
    // server-created widget on the canvas — the user clicked Save intending
    // to keep it (the X was impatience, not a real cancel). This is
    // idempotent on stale ids.
    if (selectedDashboard && widget && isPendingWidget) {
      dispatch(
        removePendingWidget({
          dashboardId: selectedDashboard.id,
          tempId: widget.id,
        }),
      );
    }
    dispatch(updateEditWidgetDrawerModalTileId(null));
  };

  const handleClose = () => {
    handleRemovePendingWidget();
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "editWidgetDrawerModal",
      }),
    );
  };

  return (
    <DrawerModal id="editWidgetDrawerModal" onClose={handleRemovePendingWidget}>
      <div className={styles[cssClassName]}>
        <div className={styles[`${cssClassName}__content`]}>
          <Title text={i18n("modules.editWidgetDrawerModal.title")} />

          <Spacer16 />

          {!widget ? (
            <p className={styles[`${cssClassName}__empty`]}>
              {i18n("modules.editWidgetDrawerModal.noWidgetSelected")}
            </p>
          ) : (
            <div className={styles[`${cssClassName}__outer-wrapper`]}>
              {widget.type === "counter-number" ? (
                <CounterNumberEditor
                  key={widget.id}
                  widget={widget}
                  isPendingWidget={isPendingWidget}
                  onClose={handleClose}
                />
              ) : widget.type === "machine-overview" ? (
                <MachineOverviewEditor
                  key={widget.id}
                  widget={widget}
                  isPendingWidget={isPendingWidget}
                  onClose={handleClose}
                />
              ) : widget.type === "trend-line" ? (
                <TrendLineEditor
                  key={widget.id}
                  widget={widget}
                  isPendingWidget={isPendingWidget}
                  onClose={handleClose}
                />
              ) : widget.type === "multi-line" ? (
                <MultiLineEditor
                  key={widget.id}
                  widget={widget}
                  isPendingWidget={isPendingWidget}
                  onClose={handleClose}
                />
              ) : widget.type === "static-header" ? (
                <StaticHeaderEditor
                  key={widget.id}
                  widget={widget}
                  isPendingWidget={isPendingWidget}
                  onClose={handleClose}
                />
              ) : widget.type === "static-text" ? (
                <StaticTextEditor
                  key={widget.id}
                  widget={widget}
                  isPendingWidget={isPendingWidget}
                  onClose={handleClose}
                />
              ) : widget.type === "static-link" ? (
                <StaticLinkEditor
                  key={widget.id}
                  widget={widget}
                  isPendingWidget={isPendingWidget}
                  onClose={handleClose}
                />
              ) : widget.type === "static-separator" ? (
                <StaticSeparatorEditor
                  key={widget.id}
                  widget={widget}
                  isPendingWidget={isPendingWidget}
                  onClose={handleClose}
                />
              ) : widget.type === "single-text-value" ? (
                <SingleTextValueEditor
                  key={widget.id}
                  widget={widget}
                  isPendingWidget={isPendingWidget}
                  onClose={handleClose}
                />
              ) : widget.type === "multiple-text-values" ? (
                <MultipleTextValuesEditor
                  key={widget.id}
                  widget={widget}
                  isPendingWidget={isPendingWidget}
                  onClose={handleClose}
                />
              ) : widget.type === "shot-data-line-chart" ? (
                <ShotDataLineChartEditor
                  key={widget.id}
                  widget={widget}
                  isPendingWidget={isPendingWidget}
                  onClose={handleClose}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </DrawerModal>
  );
};
